/**
 * PostFX renderer — draws tint overlay + vignette on a dedicated canvas.
 * Cheap Canvas2D approach: no getImageData, only fills and gradients.
 */
export class PostFx {
  constructor() {
    this._lastPreset = null;
    this._vignetteGrad = null;
    this._vignetteW = 0;
    this._vignetteH = 0;
    this._vignetteRadius = -1;
    this._vignetteStrength = -1;
  }

  /**
   * @param {CanvasRenderingContext2D} ctx - postfx canvas context
   * @param {number} w - canvas width
   * @param {number} h - canvas height
   * @param {object} preset - GradePreset (interpolated)
   * @param {number} strength - global strength multiplier 0..1
   */
  render(ctx, w, h, preset, strength) {
    if (strength <= 0 || !preset) return;

    ctx.clearRect(0, 0, w, h);

    const s = strength;

    // 1) Tint overlay using "soft-light" composite
    const { r, g, b, a } = preset.tint;
    const tintAlpha = a * s;
    if (tintAlpha > 0.001) {
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${tintAlpha})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // 2) Brightness / contrast / saturation via CSS filter on a subtle overlay
    //    We apply a very subtle brightness shift by drawing a black or white rect
    const bri = preset.brightness;
    if (Math.abs(bri - 1.0) * s > 0.005) {
      ctx.save();
      ctx.globalCompositeOperation = bri < 1.0 ? 'source-over' : 'source-over';
      if (bri < 1.0) {
        // Darken: draw semi-transparent black
        const darkAlpha = (1.0 - bri) * s;
        ctx.fillStyle = `rgba(0,0,0,${Math.min(darkAlpha, 0.3)})`;
      } else {
        // Brighten: draw semi-transparent white
        const lightAlpha = (bri - 1.0) * s;
        ctx.fillStyle = `rgba(255,255,255,${Math.min(lightAlpha, 0.15)})`;
      }
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // 3) Vignette: radial gradient from transparent center to dark edges
    const vig = preset.vignette;
    const vigStrength = vig.strength * s;
    if (vigStrength > 0.005) {
      const grad = this._getVignetteGrad(ctx, w, h, vig.radius, vigStrength);
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }
  }

  /** Cache and reuse the radial gradient when dimensions/params haven't changed. */
  _getVignetteGrad(ctx, w, h, radius, strength) {
    if (
      this._vignetteGrad &&
      this._vignetteW === w &&
      this._vignetteH === h &&
      Math.abs(this._vignetteRadius - radius) < 0.01 &&
      Math.abs(this._vignetteStrength - strength) < 0.01
    ) {
      return this._vignetteGrad;
    }
    const cx = w / 2;
    const cy = h / 2;
    const outerR = Math.sqrt(cx * cx + cy * cy);
    const innerR = outerR * radius;
    const grad = ctx.createRadialGradient(cx, cy, innerR, cx, cy, outerR);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, `rgba(0,0,0,${Math.min(strength, 0.6)})`);
    this._vignetteGrad = grad;
    this._vignetteW = w;
    this._vignetteH = h;
    this._vignetteRadius = radius;
    this._vignetteStrength = strength;
    return grad;
  }
}
